import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { EmptyState } from '../components/common/EmptyState';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { useAuth } from './AuthProvider';

/**
 * Guards `/platform/*` routes.
 *
 * Platform authority is a capability on the user (`isPlatformAdmin`), not a
 * tenant role, so this guard deliberately does NOT consult `role` — a
 * TENANT_ADMIN is not a platform administrator, and no combination of tenant
 * roles grants platform access.
 *
 * `isPlatformAdmin` here comes from `/auth/me` and is a **routing and display
 * hint only**. The authoritative check is `PlatformAdminGuard` on the server,
 * which re-reads the flag from the database on every request. A user who
 * tampered with client state would render the page and then get 403s from
 * every call it makes.
 *
 * Three deliberate behaviours:
 *  - while `/auth/me` is in flight, render a loading state rather than a denial,
 *    so protected content is never briefly exposed and a slow network is not
 *    misreported as "no access";
 *  - a non-platform user sees an explicit denial, not a silent redirect —
 *    redirecting hides misconfiguration and makes the app feel broken;
 *  - denial does NOT clear the session. The user is authenticated; they simply
 *    lack this capability.
 */
export function RequirePlatform({ children }: { children: ReactNode }) {
  const { ready, user } = useAuth();

  if (!ready) return <LoadingSkeleton rows={6} />;
  if (!user) return <Navigate to="/login" replace />;

  if (!user.isPlatformAdmin) {
    return (
      <EmptyState
        title="You do not have access"
        message="Platform administration is restricted to platform administrators. If you think this is wrong, contact your platform operator."
      />
    );
  }

  return <>{children}</>;
}
